import { useEffect, useMemo, useState } from 'react';
import { GridInitialState } from '@mui/x-data-grid-pro';
import { ProductMap, fetchProductMaps, getProductMapsSubject } from '../../../../../store/ProductMap';
import { DefaultProductMapGridColumnDefs } from '../../ColumnDefs/ProductMapGridColumnDefs';
import { UpstackDataGrid } from '../../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getInitialState } from '../../../../Compounds/UpstackDataGrid/helpers';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import {
  getCustomizationSubject,
  getDefaultPreferences,
  GridPreferences
} from '../../../../../store/UserCustomizations';
import { Icon } from '../../../../Atoms/Icon';
import { TextSpan } from '../../../../Atoms/Text';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { NewMapDialog } from '../../../../Compounds/Mapping/NewMapDialog/NewMapDialog';
import { EntityType } from '../../../../../store/Search';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../../../store/GeneralStore';
import { getProductsSubject, Product } from '../../../../../store/Product';

export const ProductMapTab = () => {
  const [productMapsLoading, setProductMapsLoading] = useState<boolean>(false);
  const [productMaps, setProductMaps] = useState<PaginatedResponse<ProductMap>>();
  const [isMapFormOpen, setIsMapFormOpen] = useState<boolean>(false);
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences()?.content['productMapGrid'] as GridPreferences
  );
  const [initialState, setInitialState] = useState<GridInitialState>();

  const filterKeys = useMemo(
    () => DefaultProductMapGridColumnDefs.filter((column) => column.filterable).map((column) => column.field),
    []
  );
  const [allProducts, setAllProducts] = useState<Product[]>([]);

  const handleFetchProductMaps = async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
    if (localLoading) setProductMapsLoading(true);
    await fetchProductMaps(filters, forceRefresh);
    if (localLoading) setProductMapsLoading(false);
  };

  useEffect(() => {
    // Subscribe to product maps
    const productMapsSub = getProductMapsSubject().subscribe((productMaps) => setProductMaps(productMaps));
    const userCustSub = getCustomizationSubject().subscribe((userCust) => {
      if (userCust) {
        setGridSettings(userCust.content.productMapGrid);

        const state = getInitialState(
          userCust.content.productMapGrid,
          DefaultProductMapGridColumnDefs,
          'productMapGrid'
        );
        setInitialState(state);

        const savedSort = state.sorting?.sortModel;
        const initialSortModel = savedSort?.length ? savedSort : undefined;
        handleFetchProductMaps(
          {
            orders: initialSortModel
              ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
              : undefined
          },
          true
        );
      }
    });

    const productsSub = getProductsSubject().subscribe((products) => {
      setAllProducts(products);
    });

    return () => {
      if (productMapsSub) productMapsSub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
      if (productsSub) productsSub.unsubscribe();
    };
  }, []);

  const handleNewMap = () => {
    setIsMapFormOpen(true);
  };

  const cardActions = () => (
    <button
      className="flex items-center space-x-2"
      onClick={handleNewMap}
      data-cy="new-product-map-button"
      data-testid="new-product-map-button">
      <Icon type="plus" />
      <TextSpan
        color="indigo"
        size="sm14">
        New Product Mapping
      </TextSpan>
    </button>
  );

  return (
    <PageCard
      dataTestId="product-map-management"
      dataCy="product-map-management"
      childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
      title="Product Map"
      cardActions={cardActions()}>
      <>
        {!initialState ||
          (productMapsLoading && (
            <div className="flex h-[45rem] m-auto">
              <ComponentSpinner label="Loading data..." />
            </div>
          ))}
        {initialState && !productMapsLoading && (
          <UpstackDataGrid
            title="Product Map"
            rows={productMaps?.data || []}
            rowCount={productMaps?.totalCount}
            columns={DefaultProductMapGridColumnDefs}
            advancedFiltering={true}
            apiFilter={true}
            fetchFunction={handleFetchProductMaps}
            filterKeys={filterKeys}
            page="productMapGrid"
            gridSettings={gridSettings}
            showSearch={false}
            initialState={initialState}
            sxStyles={{ borderWidth: '0', borderBottomLeftRadius: '.75rem', borderBottomRightRadius: '.75rem' }}
            triggerSave={false}
          />
        )}
        <NewMapDialog
          open={isMapFormOpen}
          setOpen={(open) => {
            setIsMapFormOpen(open);
            if (!open) {
              handleFetchProductMaps(undefined, true, true);
            }
          }}
          mapType={EntityType.PRODUCT}
          allProducts={allProducts}
          productMaps={productMaps?.data || []}
        />
      </>
    </PageCard>
  );
};
